<template>
  <nav ref="bottomNav" class="mobile-bottom-nav" aria-label="Navegación principal">
    <router-link
      v-for="item in items"
      :key="item.to"
      :to="item.to"
      class="mobile-bottom-nav__item"
      :class="{ 'is-active': isActive(item) }"
      :aria-label="item.label"
    >
      <i :class="item.icon" aria-hidden="true"></i>
      <span class="mobile-bottom-nav__label">{{ item.label }}</span>
    </router-link>

    <button
      type="button"
      class="mobile-bottom-nav__item mobile-bottom-nav__item--action"
      aria-label="Categorías"
      @click="openCategoriesMenu"
    >
      <i class="fas fa-layer-group" aria-hidden="true"></i>
      <span class="mobile-bottom-nav__label">Categorías</span>
    </button>
  </nav>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const bottomNav = ref(null)

const items = computed(() => [
  { to: '/', label: 'Inicio', icon: 'fas fa-house' },
  { to: '/productos', label: 'Productos', icon: 'fas fa-mobile-screen-button' },
  { to: '/iniciar-sesion', label: 'Cuenta', icon: 'fas fa-circle-user' }
])

function isActive(item) {
  if (item.to === '/') return route.path === '/'
  return route.path.startsWith(item.to.replace(/\/$/, ''))
}

function openCategoriesMenu() {
  window.dispatchEvent(new CustomEvent('mobile-menu:open'))
}

function updateBottomNavHeight() {
  const nav = bottomNav.value
  if (!nav) return

  const height = nav.getBoundingClientRect().height

  document.documentElement.style.setProperty(
    '--bottom-nav-height',
    `${height}px`
  )
}

function lockBottomNavOffset() {
  /*
    Importante:
    No usamos visualViewport para calcular el bottom.
    En iPhone/Chrome/Safari cambia al aparecer/desaparecer la barra del navegador
    y eso hace que el navbar "salte" o se mueva.
  */
  document.documentElement.style.setProperty('--bottom-nav-offset', '0px')
}

let resizeObserver = null
let cleanup = null

onMounted(async () => {
  await nextTick()

  lockBottomNavOffset()
  updateBottomNavHeight()

  resizeObserver = new ResizeObserver(() => {
    updateBottomNavHeight()
  })

  if (bottomNav.value) {
    resizeObserver.observe(bottomNav.value)
  }

  const handleResize = () => {
    lockBottomNavOffset()
    updateBottomNavHeight()
  }

  const handleOrientationChange = () => {
    setTimeout(() => {
      lockBottomNavOffset()
      updateBottomNavHeight()
    }, 250)
  }

  window.addEventListener('resize', handleResize, { passive: true })
  window.addEventListener('orientationchange', handleOrientationChange, { passive: true })

  cleanup = () => {
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('orientationchange', handleOrientationChange)

    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
  }
})

onBeforeUnmount(() => {
  if (cleanup) cleanup()
})
</script>

<style scoped>
.mobile-bottom-nav {
  position: fixed;
  left: 0.85rem;
  right: 0.85rem;
  bottom: calc(var(--bottom-nav-offset, 0px) + 0.7rem + env(safe-area-inset-bottom, 0px));
  z-index: 1200;

  display: none;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: center;

  width: auto;
  height: 68px;
  box-sizing: border-box;

  padding-top: 0.55rem;
  padding-right: 0.6rem;
  padding-bottom: 0.55rem;
  padding-left: 0.6rem;

  background: linear-gradient(
    160deg,
    rgba(20, 20, 20, 0.72) 0%,
    rgba(6, 6, 6, 0.88) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 22px;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);

  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);

  transform: translateZ(0);
  will-change: transform;

  contain: layout paint;
  outline: 1px solid rgba(255, 255, 255, 0.02);
}

@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .mobile-bottom-nav {
    background: rgba(10, 10, 10, 0.92);
  }
}

.mobile-bottom-nav__item {
  min-width: 0;

  color: rgba(255, 255, 255, 0.68);
  text-decoration: none;

  border: none;
  background: transparent;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 0.25rem;

  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;

  padding: 0.35rem 0.2rem;
  border-radius: 12px;

  cursor: pointer;

  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  transition:
    color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;
}

.mobile-bottom-nav__item i {
  font-size: 1.05rem;
  line-height: 1;
}

.mobile-bottom-nav__label {
  max-width: 100%;

  font-size: 0.65rem;
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: uppercase;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-bottom-nav__item.is-active {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.mobile-bottom-nav__item:active {
  transform: translateY(1px) scale(0.97);
}

.mobile-bottom-nav__item:active i {
  animation: nav-tap-bounce 0.28s ease;
}

@keyframes nav-tap-bounce {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.85);
  }
  100% {
    transform: scale(1);
  }
}

@media (max-width: 991.98px) {
  .mobile-bottom-nav {
    display: grid;
  }
}

@media (min-width: 992px) {
  .mobile-bottom-nav {
    display: none;
  }
}
</style>